import { SVGAttributes } from 'react';

import styled from 'styled-components';

import { Tooltip } from '@components/tooltip';
import { Color, theme } from '@styles/theme';
import { Transient } from '@utils/types';

export type BaseIconProps = SVGAttributes<any> & {
  color?: Color;
  size?: number | string;
  tooltip?: string;
  rotateDegrees?: number;
  flipHorizontal?: boolean;
  dataTestid?: string;
};

export type IconProps = Omit<BaseIconProps, 'viewBox' | 'width' | 'height'>;
export type IconSmallProps = Omit<IconProps, 'size'>;

export function BaseIcon(props: BaseIconProps) {
  const {
    size = 24,
    color,
    tooltip,
    children,
    style,
    rotateDegrees,
    flipHorizontal,
    dataTestid,
    ...rest
  } = props;

  const content = (
    <Svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      $rotateDegrees={rotateDegrees}
      $flipHorizontal={flipHorizontal}
      {...rest}
      data-testid={dataTestid}
      style={{
        ...style,

        '--icon-color': color ? theme.color[color] : undefined,
      }}
    >
      {children}
    </Svg>
  );

  if (tooltip) {
    return <Tooltip content={tooltip}>{content}</Tooltip>;
  }

  return content;
}

const Svg = styled.svg<Transient<BaseIconProps, 'rotateDegrees' | 'flipHorizontal'>>(
  ({ $rotateDegrees, $flipHorizontal }) => ({
    transition: 'all 150ms ease-out',
    color: 'var(--icon-color, currentColor)',
    flexShrink: 0,
    path: {
      transformBox: 'fill-box',
      transformOrigin: 'center',
      transform: [
        $flipHorizontal ? 'scaleX(-1)' : undefined,
        typeof $rotateDegrees === 'number' ? `rotate(${$rotateDegrees}deg)` : undefined,
      ]
        .filter(Boolean)
        .join(' '),
    },
  })
);
